import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api, formatBRL, formatDateBR, formatDateTimeBR, STATUS_LABELS, maskPhone } from "../lib/api";
import { useAuth } from "../lib/auth";
import { StatusBadge, RiskBadge } from "../components/Badges";
import {
  Users, Search, TrendingUp, Wallet, ArrowRight, AlertTriangle,
  CalendarClock, Calendar, Layers, Briefcase, Crown, UserCheck, Flame,
  CheckCircle2, LifeBuoy,
} from "lucide-react";

function KpiCard({ label, value, sub, icon: Icon, accent, testid, big }) {
  return (
    <div
      className={`fc-card ${big ? "p-5 sm:p-6" : "p-4 sm:p-5"} fc-card-hover relative overflow-hidden`}
      style={accent?.gradient ? { background: accent.gradient, borderColor: accent.border } : {}}
      data-testid={testid}
    >
      <div className="flex items-start justify-between mb-2">
        <div className={`text-[10.5px] uppercase tracking-[0.18em] font-medium ${accent?.label || "text-[#A3A3A3]"}`}>{label}</div>
        <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${accent?.icon || "border-[#262626] text-[#A3A3A3]"}`}>
          <Icon size={15} strokeWidth={1.8} />
        </div>
      </div>
      <div className={`fc-kpi-value ${big ? "text-[26px] sm:text-[30px]" : "text-[22px] sm:text-[26px]"} leading-none ${accent?.value || ""}`}>
        {value}
      </div>
      {sub && <div className="text-[11.5px] text-[#A3A3A3] mt-1.5">{sub}</div>}
    </div>
  );
}

function RankingCard({ title, icon: Icon, items, emptyMsg, accent }) {
  return (
    <div className="fc-card p-5" data-testid={`ranking-${title.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className={`w-7 h-7 rounded flex items-center justify-center border ${accent}`}>
            <Icon size={13} strokeWidth={1.8}/>
          </div>
          <h3 className="text-[13px] font-semibold tracking-tight text-[#E5E5E5]">{title}</h3>
        </div>
      </div>
      {items.length === 0 ? (
        <div className="text-[12px] text-[#737373] py-3 text-center">{emptyMsg}</div>
      ) : (
        <div className="space-y-2.5">
          {items.map((it, idx) => (
            <div key={it.id || idx} className="flex items-center gap-3">
              <div className={`w-6 h-6 rounded text-[11px] font-bold flex items-center justify-center
                ${idx === 0 ? "bg-[#D4AF37] text-black" :
                  idx === 1 ? "bg-[#888] text-black" :
                  idx === 2 ? "bg-[#A77B30] text-white" :
                  "bg-[#1a1a1a] border border-[#2a2a2a] text-[#A3A3A3]"}`}>
                {idx + 1}
              </div>
              <div className="flex-1 min-w-0">
                {it.link ? (
                  <Link to={it.link} className="text-[12.5px] text-[#E5E5E5] hover:text-[#D4AF37] truncate block">
                    {it.label}
                  </Link>
                ) : (
                  <div className="text-[12.5px] text-[#E5E5E5] truncate">{it.label}</div>
                )}
                {it.subtitle && <div className="text-[10.5px] text-[#737373] truncate">{it.subtitle}</div>}
              </div>
              <div className={`font-mono-tab text-[12.5px] font-medium ${it.valueAccent || "text-[#E5E5E5]"}`}>
                {it.value}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [exec, setExec] = useState(null);
  const [upcoming, setUpcoming] = useState(null);
  const [financialMonth, setFinancialMonth] = useState(null);
  const [collections, setCollections] = useState(null);
  const [today, setToday] = useState(null);
  const [socios, setSocios] = useState([]);
  const [indicators, setIndicators] = useState([]);

  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterIndicator, setFilterIndicator] = useState("");
  const [loading, setLoading] = useState(true);

  const isIndicador = user?.role === "indicador";
  const isAdminLike = user?.role === "admin" || user?.role === "gestora";

  const loadAll = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (filterStatus) params.status = filterStatus;
      if (filterIndicator && !isIndicador) params.indicator_id = filterIndicator;

      const calls = [
        api.get("/dashboard/stats"),
        api.get("/clients", { params }),
      ];
      if (isAdminLike) {
        calls.push(
          api.get("/dashboard/executive").catch(() => ({ data: null })),
          api.get("/dashboard/upcoming").catch(() => ({ data: null })),
          api.get("/dashboard/financial", { params: { period: "month" } }).catch(() => ({ data: null })),
          api.get("/dashboard/collections").catch(() => ({ data: null })),
          api.get("/socios").catch(() => ({ data: [] })),
          api.get("/indicators").catch(() => ({ data: [] })),
          api.get("/reports/period-results", { params: { period: "hoje" } }).catch(() => ({ data: null })),
        );
      }
      const results = await Promise.all(calls);
      setStats(results[0].data);
      setClients(results[1].data);
      if (isAdminLike) {
        setExec(results[2].data);
        setUpcoming(results[3].data);
        setFinancialMonth(results[4].data);
        setCollections(results[5].data);
        setSocios(results[6].data);
        setIndicators(results[7].data);
        setToday(results[8].data);
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    const t = setTimeout(loadAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search, filterStatus, filterIndicator]);

  // ----- Rankings -----
  const topSocios = useMemo(() => {
    if (!socios?.length) return [];
    return [...socios]
      .filter(s => s.status !== "inativo")
      .sort((a, b) => (b.capital_na_rua || 0) - (a.capital_na_rua || 0))
      .slice(0, 5)
      .map(s => ({
        id: s.id,
        label: s.name,
        subtitle: `Disponível: ${formatBRL(s.capital_disponivel)}`,
        value: formatBRL(s.capital_na_rua),
        link: `/socios`,
      }));
  }, [socios]);

  const topIndicadores = useMemo(() => {
    if (!indicators?.length || !clients?.length) return [];
    const byInd = {};
    for (const c of clients) {
      if (!c.indicator_id) continue;
      if (!byInd[c.indicator_id]) byInd[c.indicator_id] = { id: c.indicator_id, name: c.indicator_name, count: 0, total: 0 };
      byInd[c.indicator_id].count += 1;
      byInd[c.indicator_id].total += Number(c.approved_amount || c.requested_amount || 0);
    }
    return Object.values(byInd)
      .sort((a, b) => b.total - a.total)
      .slice(0, 5)
      .map(i => ({
        id: i.id,
        label: i.name || "—",
        subtitle: `${i.count} cliente(s)`,
        value: formatBRL(i.total),
        link: `/indicadores`,
      }));
  }, [clients, indicators]);

  const topAtrasos = useMemo(() => {
    const items = collections?.items || [];
    if (!items.length) return [];
    return [...items]
      .sort((a, b) => ((b.capital || 0) + (b.juros || 0)) - ((a.capital || 0) + (a.juros || 0)))
      .slice(0, 5)
      .map(it => ({
        id: it.id || `${it.source}-${it.client_id}`,
        label: it.client_name || "—",
        subtitle: `${it.days_overdue} dia(s) — ${it.source === "installment" ? "Parcela" : "Contrato"}`,
        value: formatBRL((it.capital || 0) + (it.juros || 0)),
        valueAccent: "text-red-300",
        link: it.client_id ? `/clientes/${it.client_id}` : undefined,
      }));
  }, [collections]);

  // ----- KPI helpers -----
  const taxaInadimplencia = exec?.taxa_inadimplencia || 0;
  const inadAccentLevel = taxaInadimplencia >= 10 ? "danger" : taxaInadimplencia >= 5 ? "warn" : "ok";

  return (
    <Layout>
      <div className="flex items-end justify-between mb-7">
        <div>
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#D4AF37]">Painel executivo</div>
          <h1 className="font-display text-3xl sm:text-[34px] font-bold tracking-tight mt-1.5">
            Olá, {user?.name?.split(" ")[0]}.
          </h1>
          <p className="text-[#A3A3A3] text-sm mt-1">
            {isIndicador ? "Acompanhe os clientes que você indicou." : "Visão geral consolidada da operação."}
          </p>
        </div>
      </div>

      {/* === FAIXA OPERACIONAL SUPERIOR (admin/gestora — pulso do dia) === */}
      {isAdminLike && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5" data-testid="daily-strip">
          <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2.5" data-testid="strip-recebido-hoje">
            <div className="text-[9.5px] uppercase tracking-wider text-emerald-300">Recebido hoje</div>
            <div className="font-mono-tab text-[15px] text-emerald-300 mt-0.5">{today ? `R$ ${(today.juros_recebidos + today.capital_recebido).toLocaleString("pt-BR", {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : "—"}</div>
          </div>
          <div className="rounded-md border border-[#D4AF37]/30 bg-[#D4AF37]/5 px-3 py-2.5" data-testid="strip-vence-hoje">
            <div className="text-[9.5px] uppercase tracking-wider text-[#D4AF37]">Vence hoje</div>
            <div className="font-mono-tab text-[15px] text-[#D4AF37] mt-0.5">{upcoming?.vence_hoje ?? "—"}</div>
          </div>
          <div className="rounded-md border border-cyan-500/30 bg-cyan-500/5 px-3 py-2.5" data-testid="strip-vence-amanha">
            <div className="text-[9.5px] uppercase tracking-wider text-cyan-300">Vence amanhã</div>
            <div className="font-mono-tab text-[15px] text-cyan-300 mt-0.5">{upcoming?.vence_amanha ?? "—"}</div>
          </div>
          <div className="rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2.5" data-testid="strip-atraso">
            <div className="text-[9.5px] uppercase tracking-wider text-red-300">Em atraso</div>
            <div className="font-mono-tab text-[15px] text-red-300 mt-0.5">{collections?.contratos_em_atraso ?? "—"}</div>
          </div>
          <div className="rounded-md border border-fuchsia-500/30 bg-fuchsia-500/5 px-3 py-2.5" data-testid="strip-novos">
            <div className="text-[9.5px] uppercase tracking-wider text-fuchsia-300">Novos hoje</div>
            <div className="font-mono-tab text-[15px] text-fuchsia-300 mt-0.5">{today?.contratos_novos ?? "—"}</div>
          </div>
        </div>
      )}

      {/* === Bloco executivo (apenas admin/gestora) === */}
      {isAdminLike && (
        <>
          {/* HERO — Capital na rua (card principal, full-width, valor gigante) */}
          <div
            className="fc-card fc-card-hover p-6 sm:p-8 mb-4 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(212,175,55,0.18) 0%, rgba(212,175,55,0.06) 35%, rgba(60,40,10,0.04) 70%, rgba(0,0,0,0) 100%)",
              borderColor: "rgba(212,175,55,0.50)",
            }}
            data-testid="kpi-capital-na-rua"
          >
            <div className="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-30 pointer-events-none"
              style={{ background: "radial-gradient(circle, rgba(212,175,55,0.35) 0%, rgba(212,175,55,0) 70%)" }} />
            <div className="grid lg:grid-cols-3 gap-6 items-end relative">
              <div className="lg:col-span-2">
                <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold flex items-center gap-2">
                  <Wallet size={13} strokeWidth={1.8}/> Capital na rua
                </div>
                <div className="fc-kpi-value font-display text-[44px] sm:text-[58px] lg:text-[68px] leading-none text-[#E8C547] mt-3 font-bold tracking-tight">
                  {formatBRL(exec?.capital_total_exposto)}
                </div>
                <div className="text-[12.5px] text-[#A3A3A3] mt-3">
                  Total exposto na operação · <span className="text-[#D4AF37]">{exec?.contratos_ativos ?? 0}</span> contratos
                  · <span className="text-[#D4AF37]">{exec?.parcelados_ativos ?? 0}</span> parcelados/acordos
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 lg:gap-4">
                <div className="bg-black/30 rounded-md p-3.5 border border-emerald-500/25">
                  <div className="text-[10px] uppercase tracking-wider text-emerald-300/80">Próprio</div>
                  <div className="font-mono-tab text-[16px] text-emerald-200 mt-1 font-semibold">
                    {formatBRL(exec?.capital_proprio_total_exposto)}
                  </div>
                </div>
                <div className="bg-black/30 rounded-md p-3.5 border border-indigo-500/25">
                  <div className="text-[10px] uppercase tracking-wider text-indigo-300/80">Sócios</div>
                  <div className="font-mono-tab text-[16px] text-indigo-200 mt-1 font-semibold">
                    {formatBRL((exec?.capital_total_exposto || 0) - (exec?.capital_proprio_total_exposto || 0))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Linha executiva — Saudável × Recuperação (iter69) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <KpiCard
              label="Capital Saudável"
              value={formatBRL(exec?.capital_saudavel_total)}
              sub={`Próprio: ${formatBRL(exec?.capital_proprio_saudavel)} · Sócios: ${formatBRL(exec?.capital_socios_saudavel)}`}
              icon={CheckCircle2} testid="kpi-capital-saudavel" big
              accent={{
                border: "rgba(16,185,129,0.35)",
                gradient: "linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(20,83,45,0.04) 60%, rgba(0,0,0,0) 100%)",
                label: "text-emerald-300", icon: "border-emerald-500/40 text-emerald-300", value: "text-emerald-200",
              }}
            />
            <KpiCard
              label="Capital em Recuperação"
              value={formatBRL(exec?.capital_recuperacao_total_split ?? exec?.capital_recuperacao_total)}
              sub={`Próprio: ${formatBRL(exec?.capital_proprio_recuperacao)} · Sócios: ${formatBRL(exec?.capital_socios_recuperacao)} · ${(exec?.qtd_contratos_recuperacao || 0) + (exec?.qtd_parcelados_recuperacao || 0)} op.`}
              icon={LifeBuoy} testid="kpi-capital-recuperacao" big
              accent={{
                border: "rgba(245,158,11,0.35)",
                gradient: "linear-gradient(135deg, rgba(245,158,11,0.10) 0%, rgba(80,50,10,0.04) 60%, rgba(0,0,0,0) 100%)",
                label: "text-amber-300", icon: "border-amber-500/40 text-amber-300", value: "text-amber-200",
              }}
            />
          </div>

          {/* Linha executiva — 3 cards complementares */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <KpiCard
              label="Capital Próprio"
              value={formatBRL(exec?.capital_proprio_total_exposto)}
              sub={`${exec?.contratos_proprios_ativos ?? 0} contratos · ${exec?.parcelados_proprios_ativos ?? 0} parcelados`}
              icon={Briefcase} testid="kpi-capital-proprio" big
              accent={{
                border: "rgba(16,185,129,0.35)",
                gradient: "linear-gradient(135deg, rgba(16,185,129,0.10) 0%, rgba(20,83,45,0.04) 60%, rgba(0,0,0,0) 100%)",
                label: "text-emerald-300", icon: "border-emerald-500/40 text-emerald-300", value: "text-emerald-200",
              }}
            />
            <KpiCard
              label="Capital de Sócios"
              value={formatBRL((exec?.capital_total_exposto || 0) - (exec?.capital_proprio_total_exposto || 0))}
              sub="Operações com aporte externo"
              icon={Users} testid="kpi-capital-socios" big
              accent={{
                border: "rgba(99,102,241,0.35)",
                gradient: "linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(30,27,75,0.04) 60%, rgba(0,0,0,0) 100%)",
                label: "text-indigo-300", icon: "border-indigo-500/40 text-indigo-300", value: "text-indigo-200",
              }}
            />
            <KpiCard
              label="Inadimplência"
              value={`${taxaInadimplencia.toFixed(1)}%`}
              sub={`${formatBRL(exec?.inadimplencia_total)} · ${exec?.clientes_inadimplentes ?? 0} clientes`}
              icon={AlertTriangle} testid="kpi-inadimplencia" big
              accent={
                inadAccentLevel === "danger" ? {
                  border: "rgba(239,68,68,0.55)",
                  gradient: "linear-gradient(135deg, rgba(239,68,68,0.18) 0%, rgba(120,20,20,0.08) 50%, rgba(0,0,0,0) 100%)",
                  label: "text-red-300", icon: "border-red-500/60 text-red-300", value: "text-red-200",
                } : inadAccentLevel === "warn" ? {
                  border: "rgba(245,158,11,0.50)",
                  gradient: "linear-gradient(135deg, rgba(245,158,11,0.15) 0%, rgba(80,50,10,0.05) 50%, rgba(0,0,0,0) 100%)",
                  label: "text-amber-300", icon: "border-amber-500/55 text-amber-300", value: "text-amber-200",
                } : {
                  border: "rgba(16,185,129,0.40)",
                  gradient: "linear-gradient(135deg, rgba(16,185,129,0.12) 0%, rgba(0,0,0,0) 100%)",
                  label: "text-emerald-300", icon: "border-emerald-500/50 text-emerald-300", value: "text-emerald-200",
                }
              }
            />
          </div>

          {/* Linha 2 — Operação do dia (Vence hoje + Próximos 7d com ALERTA visual) */}
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#737373] mb-2.5">Operação do dia</div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
            <div
              className={`fc-card fc-card-hover p-5 relative overflow-hidden ${(upcoming?.vence_hoje || 0) > 0 ? "alert-pulse" : ""}`}
              style={(upcoming?.vence_hoje || 0) > 0 ? {
                background: "linear-gradient(135deg, rgba(245,158,11,0.18) 0%, rgba(120,70,10,0.08) 50%, rgba(0,0,0,0) 100%)",
                borderColor: "rgba(245,158,11,0.60)",
                boxShadow: "0 0 0 1px rgba(245,158,11,0.25), 0 8px 24px -8px rgba(245,158,11,0.30)",
              } : {}}
              data-testid="kpi-vence-hoje"
            >
              <div className="flex items-start justify-between mb-2">
                <div className={`text-[10.5px] uppercase tracking-[0.18em] font-semibold ${(upcoming?.vence_hoje || 0) > 0 ? "text-amber-300" : "text-[#A3A3A3]"}`}>
                  ⚠ Vence hoje
                </div>
                <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${(upcoming?.vence_hoje || 0) > 0 ? "border-amber-500/60 text-amber-300 bg-amber-500/10" : "border-[#262626] text-[#A3A3A3]"}`}>
                  <CalendarClock size={15} strokeWidth={1.8}/>
                </div>
              </div>
              <div className={`fc-kpi-value text-[26px] sm:text-[30px] leading-none ${(upcoming?.vence_hoje || 0) > 0 ? "text-amber-200" : ""}`}>
                {upcoming?.vence_hoje ?? "—"}
              </div>
              <div className="text-[11.5px] text-[#A3A3A3] mt-1.5">
                {upcoming?.vence_hoje_contratos ?? 0} contratos · {upcoming?.vence_hoje_parcelas ?? 0} parcelas
              </div>
            </div>

            <div
              className="fc-card fc-card-hover p-5 relative overflow-hidden"
              style={(upcoming?.proximos_7_dias || 0) > 0 ? {
                background: "linear-gradient(135deg, rgba(234,179,8,0.10) 0%, rgba(60,40,10,0.04) 60%, rgba(0,0,0,0) 100%)",
                borderColor: "rgba(234,179,8,0.40)",
              } : {}}
              data-testid="kpi-proximos-7"
            >
              <div className="flex items-start justify-between mb-2">
                <div className={`text-[10.5px] uppercase tracking-[0.18em] font-semibold ${(upcoming?.proximos_7_dias || 0) > 0 ? "text-yellow-300" : "text-[#A3A3A3]"}`}>
                  📅 Próximos 7 dias
                </div>
                <div className={`w-8 h-8 rounded-md flex items-center justify-center border ${(upcoming?.proximos_7_dias || 0) > 0 ? "border-yellow-500/45 text-yellow-300 bg-yellow-500/10" : "border-[#262626] text-[#A3A3A3]"}`}>
                  <Calendar size={15} strokeWidth={1.8}/>
                </div>
              </div>
              <div className={`fc-kpi-value text-[26px] sm:text-[30px] leading-none ${(upcoming?.proximos_7_dias || 0) > 0 ? "text-yellow-200" : ""}`}>
                {upcoming?.proximos_7_dias ?? "—"}
              </div>
              <div className="text-[11.5px] text-[#A3A3A3] mt-1.5">
                {upcoming?.proximos_7_dias_contratos ?? 0} contratos · {upcoming?.proximos_7_dias_parcelas ?? 0} parcelas
              </div>
            </div>

            <KpiCard
              label="Parcelados / Acordos"
              value={exec?.parcelados_ativos ?? "—"}
              sub="Em curso"
              icon={Layers} testid="kpi-parcelados-ativos"
              accent={{ label: "text-cyan-300", icon: "border-cyan-500/30 text-cyan-300" }}
            />
            <KpiCard
              label="Juros recebidos (mês)"
              value={formatBRL(financialMonth?.juros_recebidos_periodo)}
              sub={`Capital recuperado: ${formatBRL(financialMonth?.capital_recuperado_periodo)}`}
              icon={TrendingUp} testid="kpi-juros-mes"
              accent={{ label: "text-emerald-300", icon: "border-emerald-500/40 text-emerald-300", value: "text-emerald-200" }}
            />
          </div>

          {/* Linha 3 — Rankings */}
          <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#737373] mb-2.5">Rankings rápidos</div>
          <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-3 mb-8">
            <RankingCard
              title="Top 5 indicadores"
              icon={UserCheck}
              accent="border-[#D4AF37]/40 text-[#D4AF37]"
              items={topIndicadores}
              emptyMsg="Sem indicadores ativos"
            />
            <RankingCard
              title="Top 5 maiores carteiras"
              icon={Crown}
              accent="border-amber-500/40 text-amber-300"
              items={topSocios}
              emptyMsg="Sem carteiras cadastradas"
            />
            <RankingCard
              title="Top 5 em atraso"
              icon={Flame}
              accent="border-red-500/40 text-red-300"
              items={topAtrasos}
              emptyMsg="Sem inadimplência — parabéns!"
            />
          </div>
        </>
      )}

      {/* === Bloco indicador (somente role=indicador) === */}
      {isIndicador && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mb-7">
          <KpiCard label="Total de clientes" value={stats?.total ?? "—"} icon={Users} testid="kpi-total" big
            accent={{ label: "text-[#D4AF37]", icon: "border-[#D4AF37]/40 text-[#D4AF37]" }} />
          <KpiCard label="Aprovados" value={stats?.aprovado ?? "—"} icon={UserCheck} testid="kpi-aprovado"
            accent={{ label: "text-emerald-300", icon: "border-emerald-500/40 text-emerald-300" }} />
          <KpiCard label="Em análise" value={stats?.em_analise ?? "—"} icon={CalendarClock} testid="kpi-em_analise"
            accent={{ label: "text-amber-300", icon: "border-amber-500/30 text-amber-300" }} />
          <KpiCard label="Minha comissão" value={formatBRL(stats?.valor_comissao)} icon={Wallet} testid="kpi-valor_comissao"
            accent={{ label: "text-[#D4AF37]", icon: "border-[#D4AF37]/40 text-[#D4AF37]" }} />
        </div>
      )}

      {/* === Clientes recentes (compacto) === */}
      <div className="mt-2 mb-4 flex flex-col lg:flex-row gap-3 lg:items-center lg:justify-between">
        <div>
          <h2 className="font-display text-xl font-semibold">Clientes recentes</h2>
          <p className="text-[12px] text-[#737373] mt-0.5">{clients.length} resultado(s)</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 lg:items-center">
          <div className="relative">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]"/>
            <input
              className="fc-input pl-9 min-w-[240px]"
              placeholder="Buscar nome, CPF, WhatsApp..."
              value={search} onChange={e=>setSearch(e.target.value)}
              data-testid="clients-search"
            />
          </div>
          <select value={filterStatus} onChange={(e)=>setFilterStatus(e.target.value)} className="fc-input min-w-[160px]" data-testid="filter-status">
            <option value="">Todos status</option>
            {Object.entries(STATUS_LABELS).map(([v,l])=>(<option key={v} value={v}>{l}</option>))}
          </select>
          {!isIndicador && (
            <select value={filterIndicator} onChange={e=>setFilterIndicator(e.target.value)} className="fc-input min-w-[180px]" data-testid="filter-indicator">
              <option value="">Todos indicadores</option>
              {indicators.map(i => (<option key={i.id} value={i.id}>{i.name}</option>))}
            </select>
          )}
        </div>
      </div>

      {/* Mobile cards */}
      <div className="md:hidden space-y-3" data-testid="clients-mobile-list">
        {loading && [1,2,3].map(i => <div key={i} className="fc-card h-28 shimmer"/>)}
        {!loading && clients.length === 0 && (
          <div className="fc-card p-8 text-center text-[#737373]">Nenhum cliente encontrado.</div>
        )}
        {!loading && clients.map(c => (
          <Link key={c.id} to={`/clientes/${c.id}`} className="block fc-card p-4 fc-card-hover" data-testid={`client-card-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-medium truncate">{c.full_name}</div>
                <div className="text-[12px] text-[#A3A3A3] mt-0.5">{maskPhone(c.whatsapp)}</div>
                {!isIndicador && c.indicator_name && (
                  <div className="text-[11px] text-[#737373] mt-0.5">Indicador: {c.indicator_name}</div>
                )}
              </div>
              <ArrowRight size={16} className="text-[#737373] flex-shrink-0"/>
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#1f1f1f]">
              <div>
                <div className="text-[10px] uppercase tracking-wider text-[#737373]">Solicitado</div>
                <div className="font-mono-tab text-sm mt-0.5">{formatBRL(c.requested_amount)}</div>
              </div>
              {!isIndicador && (
                <div>
                  <div className="text-[10px] uppercase tracking-wider text-[#737373]">Aprovado</div>
                  <div className="font-mono-tab text-sm mt-0.5 text-[#D4AF37]">{formatBRL(c.approved_amount)}</div>
                </div>
              )}
              <StatusBadge status={c.status}/>
            </div>
            <div className="text-[11px] text-[#737373] mt-2.5">{formatDateBR(c.created_at)}</div>
          </Link>
        ))}
      </div>

      {/* Desktop table */}
      <div className="fc-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="clients-table">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#A3A3A3] border-b border-[#1f1f1f]">
                <th className="py-3 px-4">Data</th>
                <th className="py-3 px-4">Nome</th>
                {!isIndicador && <th className="py-3 px-4">CPF</th>}
                <th className="py-3 px-4">WhatsApp</th>
                {!isIndicador && <th className="py-3 px-4">Indicador</th>}
                <th className="py-3 px-4">Solicitado</th>
                {!isIndicador && <th className="py-3 px-4">Aprovado</th>}
                <th className="py-3 px-4">Status</th>
                {!isIndicador && <th className="py-3 px-4">Risco</th>}
                <th className="py-3 px-4">Atualizado</th>
                <th className="py-3 px-4 text-right pr-6">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && [1,2,3,4].map(i => (
                <tr key={i} className="border-b border-[#1f1f1f]">
                  <td colSpan={isIndicador ? 7 : 11} className="py-3.5 px-4">
                    <div className="h-5 shimmer rounded"/>
                  </td>
                </tr>
              ))}
              {!loading && clients.length === 0 && (
                <tr><td colSpan={isIndicador ? 7 : 11} className="py-12 text-center text-[#737373]">Nenhum cliente encontrado.</td></tr>
              )}
              {!loading && clients.map(c => (
                <tr key={c.id} className="border-b border-[#1f1f1f] hover:bg-[#121212] transition-colors" data-testid={`client-row-${c.id}`}>
                  <td className="py-3 px-4 text-[#A3A3A3] whitespace-nowrap">{formatDateBR(c.created_at)}</td>
                  <td className="py-3 px-4 font-medium">{c.full_name}</td>
                  {!isIndicador && <td className="py-3 px-4 text-[#A3A3A3] font-mono-tab">{c.cpf ? c.cpf.replace(/^(\d{3})(\d{3})(\d{3})(\d{2})$/,"$1.$2.$3-$4") : "—"}</td>}
                  <td className="py-3 px-4 text-[#A3A3A3]">{maskPhone(c.whatsapp)}</td>
                  {!isIndicador && <td className="py-3 px-4 text-[#A3A3A3]">{c.indicator_name || "—"}</td>}
                  <td className="py-3 px-4 font-mono-tab">{formatBRL(c.requested_amount)}</td>
                  {!isIndicador && <td className="py-3 px-4 font-mono-tab text-[#D4AF37]">{formatBRL(c.approved_amount)}</td>}
                  <td className="py-3 px-4"><StatusBadge status={c.status}/></td>
                  {!isIndicador && <td className="py-3 px-4"><RiskBadge risk={c.risk_level}/></td>}
                  <td className="py-3 px-4 text-[#737373] whitespace-nowrap">{formatDateTimeBR(c.updated_at)}</td>
                  <td className="py-3 px-4 text-right pr-6">
                    <Link to={`/clientes/${c.id}`} className="inline-flex items-center gap-1.5 text-[#D4AF37] hover:underline text-[13px]" data-testid={`view-client-${c.id}`}>
                      Abrir <ArrowRight size={13}/>
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
